import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { StatusChip } from '@smena/ui';
import { PageHeader } from '@/components/PageHeader';
import { QueryState } from '@/components/QueryState';
import { Tabs, type TabItem } from '@/components/Tabs';
import { fetchAccount } from '@/features/accounts/api';
import { IntegrationsTab } from '@/features/accounts/IntegrationsTab';
import { OverviewTab } from '@/features/accounts/OverviewTab';
import { ActivityTab } from '@/features/troubleshoot/ActivityTab';
import { DialogsTab } from '@/features/troubleshoot/DialogsTab';
import { AuditTable } from '@/features/troubleshoot/AuditTable';
import { AccountPaymentsTab } from '@/features/payments/AccountPaymentsTab';

// Активная вкладка живёт в URL (?tab=integrations), чтобы ссылку можно было кинуть коллеге
// прямо на нужный срез аккаунта.
const TABS: TabItem[] = [
  { key: 'overview', label: 'Обзор' },
  { key: 'dialogs', label: 'Диалоги' },
  { key: 'activity', label: 'Активность' },
  { key: 'integrations', label: 'Интеграции' },
  { key: 'payments', label: 'Платежи' },
  { key: 'audit', label: 'Аудит' },
];

export function AccountPage() {
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'overview';

  const q = useQuery({
    queryKey: ['account', id],
    queryFn: () => fetchAccount(id),
    enabled: Boolean(id),
  });

  return (
    <>
      <PageHeader
        title={q.data?.name ?? 'Аккаунт'}
        subtitle={q.data ? `${q.data.slug} · создан ${new Date(q.data.createdAt).toLocaleDateString('ru-RU')}` : undefined}
        actions={
          <>
            {q.data ? (
              <StatusChip tone={q.data.kind === 'demo' ? 'muted' : 'accent'}>{q.data.kind}</StatusChip>
            ) : null}
            <Link to="/accounts" className="text-sm text-accent-fg hover:underline">
              ко всем аккаунтам
            </Link>
          </>
        }
      />
      <QueryState isLoading={q.isLoading} error={q.error}>
        {q.data ? (
          <>
            <Tabs
              items={TABS}
              value={tab}
              onChange={(key) => setParams(key === 'overview' ? {} : { tab: key }, { replace: true })}
            />
            {tab === 'dialogs' ? <DialogsTab accountId={q.data.id} /> : null}
            {tab === 'activity' ? <ActivityTab accountId={q.data.id} /> : null}
            {tab === 'integrations' ? <IntegrationsTab account={q.data} /> : null}
            {tab === 'payments' ? <AccountPaymentsTab accountId={q.data.id} /> : null}
            {tab === 'audit' ? <AuditTable scope="account" accountId={q.data.id} /> : null}
            {tab === 'overview' ? <OverviewTab account={q.data} /> : null}
          </>
        ) : null}
      </QueryState>
    </>
  );
}
